const MAX_SIGNIFICANT_DIGITS = 65;
const MAX_FRACTION_DIGITS = 30;
const INVALID_DECIMAL_PATTERN = /[eE+-]/;

export interface ParseDecimalResult {
  valid: boolean;
  normalized: string;
}

export const normalizeDecimalInput = (value: string): string => {
  const trimmed = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return '';

  const [integerPart = '', fractionPart = ''] = trimmed.split('.');
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '') || (fractionPart ? '0' : '0');
  const normalizedFraction = fractionPart.slice(0, MAX_FRACTION_DIGITS);

  if (!normalizedFraction) {
    return normalizedInteger === '0' && !integerPart ? '' : normalizedInteger;
  }

  return `${normalizedInteger}.${normalizedFraction}`;
};

export const parsePositiveDecimal = (value: string | null | undefined): ParseDecimalResult => {
  if (value == null) return { valid: false, normalized: '' };

  const normalized = normalizeDecimalInput(String(value));
  if (!normalized || INVALID_DECIMAL_PATTERN.test(normalized)) {
    return { valid: false, normalized: '' };
  }

  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return { valid: false, normalized: '' };
  }

  const digits = normalized.replace('.', '').replace(/^0+/, '') || '0';
  if (digits.length > MAX_SIGNIFICANT_DIGITS) {
    return { valid: false, normalized: '' };
  }

  const fractionLength = normalized.includes('.') ? normalized.split('.')[1]?.length ?? 0 : 0;
  if (fractionLength > MAX_FRACTION_DIGITS) {
    return { valid: false, normalized: '' };
  }

  if (/^0+(\.0+)?$/.test(normalized)) {
    return { valid: false, normalized: '' };
  }

  return { valid: true, normalized };
};

export const parseNonNegativeDecimal = (value: string | null | undefined): ParseDecimalResult => {
  const parsed = parsePositiveDecimal(value);
  if (parsed.valid) return parsed;

  const normalized = normalizeDecimalInput(String(value ?? ''));
  if (normalized === '0' || normalized === '0.0') {
    return { valid: true, normalized: '0' };
  }

  return parsed;
};

const splitDecimalParts = (value: string): { integer: string; fraction: string } => {
  const [integer = '0', fraction = ''] = value.split('.');
  return { integer, fraction };
};

const compareIntegerStrings = (left: string, right: string): number => {
  const leftNormalized = left.replace(/^0+/, '') || '0';
  const rightNormalized = right.replace(/^0+/, '') || '0';

  if (leftNormalized.length !== rightNormalized.length) {
    return leftNormalized.length > rightNormalized.length ? 1 : -1;
  }

  if (leftNormalized === rightNormalized) return 0;
  return leftNormalized > rightNormalized ? 1 : -1;
};

export const compareDecimal = (left: string, right: string): -1 | 0 | 1 => {
  const leftParsed = parseNonNegativeDecimal(left);
  const rightParsed = parseNonNegativeDecimal(right);

  if (!leftParsed.valid || !rightParsed.valid) return 0;

  const leftParts = splitDecimalParts(leftParsed.normalized);
  const rightParts = splitDecimalParts(rightParsed.normalized);
  const integerCompare = compareIntegerStrings(leftParts.integer, rightParts.integer);

  if (integerCompare !== 0) return integerCompare as -1 | 0 | 1;

  const maxFraction = Math.max(leftParts.fraction.length, rightParts.fraction.length);
  const leftFraction = leftParts.fraction.padEnd(maxFraction, '0');
  const rightFraction = rightParts.fraction.padEnd(maxFraction, '0');

  if (leftFraction === rightFraction) return 0;
  return (leftFraction > rightFraction ? 1 : -1) as -1 | 1;
};

export const subtractNonNegativeDecimal = (total: string, part: string): string | null => {
  const left = parseNonNegativeDecimal(total);
  const right = parseNonNegativeDecimal(part);
  if (!left.valid || !right.valid || compareDecimal(left.normalized, right.normalized) < 0) return null;

  const leftFraction = left.normalized.split('.')[1]?.length ?? 0;
  const rightFraction = right.normalized.split('.')[1]?.length ?? 0;
  const scale = Math.max(leftFraction, rightFraction);
  const toScaled = (value: string) => {
    const [integer = '0', fraction = ''] = value.split('.');
    return BigInt(integer + fraction.padEnd(scale, '0'));
  };
  const diff = toScaled(left.normalized) - toScaled(right.normalized);
  if (scale === 0) return diff.toString();
  const raw = diff.toString().padStart(scale + 1, '0');
  const head = raw.slice(0, raw.length - scale);
  const tail = raw.slice(raw.length - scale).replace(/0+$/, '');
  return tail ? `${head}.${tail}` : head;
};

export const isDecimalGreaterThan = (left: string, right: string): boolean => compareDecimal(left, right) === 1;

export const isDecimalLessThan = (left: string, right: string): boolean => compareDecimal(left, right) === -1;

export const normalizeDecimalForPayload = (value: string): string => {
  const parsed = parsePositiveDecimal(value);
  if (parsed.valid) return parsed.normalized;

  const nonNegative = parseNonNegativeDecimal(value);
  return nonNegative.valid ? nonNegative.normalized : value.trim();
};
